import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  setCurrentAge,
  setRetirementAge,
  setContributionYears,
  setMonthlyContribution,
  setAnnualIncrease,
  setExpectedReturn,
  setAnnuityInvestment,
  setAnnuityRate,
  setTotalTaxSaving,
  setMonthlyPension,
  resetPentionData,
  setPensionErrors,
} from "../redux/calculations/pensionSlice";
import {
  resetChartData,
  updatedPensionChart,
} from "../redux/calculations/calculaterSlice";
import arrowImage from "../assets/svg/arrow.svg";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import DecimalUtils from "../utiles/decimalFormate";
import { Helmet } from "react-helmet";

const PensionCalc = () => {
  const [result, setResult] = useState(null);
  const [contributionUnit, setContributionUnit] = useState("Month");
  const { t, textData, timeUnits } = useLocale();
  const { pensionCalc } = textData;
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const dispatch = useDispatch();
  const [calcDone, setCalcDone] = useState(false);
  const {
    currentAge,
    retirementAge,
    contributionYears,
    monthlyContribution,
    annualIncrease,
    expectedReturn,
    annuityInvestment,
    annuityRate,
    pensionErrors,
  } = useSelector((state) => state.pensionCalculator);

  const formik = useFormik({
    initialValues: {
      currentAge: currentAge || 18,
      retirementAge: retirementAge || 50,
      contributionYears: contributionYears || 40,
      monthlyContribution: monthlyContribution || 1000,
      annualIncrease: annualIncrease || 2,
      expectedReturn: expectedReturn || 10,
      annuityInvestment: annuityInvestment || 56,
      annuityRate: annuityRate || 7,
    },
    validationSchema: Yup.object({
      currentAge: Yup.number()
        .min(18, pensionCalc.currentAgeMin)
        .max(70, pensionCalc.currentAgeMax)
        .required(pensionCalc.currentAgeRequired),
      retirementAge: Yup.number()
        .min(50, pensionCalc.investUpToAgeMin)
        .max(75, pensionCalc.investUpToAgeMax)
        .required(pensionCalc.investUpToAgeRequired),
      // .test(
      //   "retirement-greater-than-current",
      //   textData.errorRetirementAgeGreater,
      //   function (value) {
      //     const { currentAge } = this.parent;
      //     return value > currentAge;
      //   }
      // ),
      contributionYears: Yup.number()
        // .min( 0, pensionCalc.totalContributionYearsMin)
        // .max(100, pensionCalc.totalContributionYearsMax)
        .required(pensionCalc.totalContributionYearsRequired),
      monthlyContribution: Yup.number()
        .min(100, pensionCalc.totalContributionYearsMin)
        .max(1000000, pensionCalc.totalContributionYearsMax)
        .required(pensionCalc.contributionAmountRequired),
      annualIncrease: Yup.number()
        .min(0, pensionCalc.annualIncreaseMin)
        .max(15, pensionCalc.annualIncreaseMax)
        .required(pensionCalc.annualIncreaseRequired),
      expectedReturn: Yup.number()
        .min(5, pensionCalc.expectedReturnMin)
        .max(15, pensionCalc.expectedReturnMax)
        .required(pensionCalc.expectedReturnRequired),
      annuityInvestment: Yup.number()
        .min(40, pensionCalc.investInAnnuityMin)
        .max(100, pensionCalc.investInAnnuityMax)
        .required(pensionCalc.investInAnnuityRequired),
      annuityRate: Yup.number()
        .min(4, pensionCalc.annuityRateMin)
        .max(10, pensionCalc.annuityRateMax)
        .required(pensionCalc.annuityRateRequired),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_PENSION");
      }
      const age = Number(values.currentAge);
      const retirementAge = Number(values.retirementAge);
      const contributionYears = Number(values.contributionYears);
      let contribution = Number(values.monthlyContribution);
      const returnRate = Number(values.expectedReturn) / 100;
      const annuityPercentage = Number(values.annuityInvestment) / 100;
      const annuityRate = Number(values.annuityRate) / 100;
      const annualIncrease = Number(values.annualIncrease) / 100;

      const isMonthlyContribution = contributionUnit === "Month";
      const totalPayments = isMonthlyContribution
        ? contributionYears * 12
        : contributionYears;
      const periodicRate = returnRate / (isMonthlyContribution ? 12 : 1);
      if (currentAge > retirementAge) {
        dispatch(setPensionErrors(textData.errorRetirementAgeGreater));
        setTimeout(() => {
          dispatch(setPensionErrors(null));
        }, 3000);
        return;
      }
      dispatch(setPensionErrors(null));
      let totalInvestment = 0;
      let corpusAtRetirement = 0;

      const monthlyData = [];

      for (let i = 0; i < totalPayments; i++) {
        corpusAtRetirement =
          (corpusAtRetirement + contribution) * (1 + periodicRate);
        totalInvestment += contribution;

        if (
          (isMonthlyContribution && (i + 1) % 12 === 0) ||
          !isMonthlyContribution
        ) {
          monthlyData.push({
            year: Math.floor(i / 12) + 1,
            investment: totalInvestment,
            corpus: corpusAtRetirement,
          });
        }

        if (
          (isMonthlyContribution && (i + 1) % 12 === 0) ||
          !isMonthlyContribution
        ) {
          contribution *= 1 + annualIncrease;
        }
      }

      const interestEarned = corpusAtRetirement - totalInvestment;

      const annuityValue = corpusAtRetirement * annuityPercentage;

      const lumpsumValue = corpusAtRetirement - annuityValue;

      const annuityPayments = (retirementAge - age) * 12;
      const monthlyAnnuityRate = annuityRate / 12;
      const monthlyPension =
        (annuityValue * monthlyAnnuityRate) /
        (1 - Math.pow(1 + monthlyAnnuityRate, -annuityPayments));

      const maxTaxSavingPerYear = 200000;
      const annualTaxSaving = Math.min(
        maxTaxSavingPerYear,
        contribution * (isMonthlyContribution ? 12 : 1)
      );
      const totalTaxSaving = annualTaxSaving * contributionYears;

      dispatch(setMonthlyPension(monthlyPension));

      dispatch(setTotalTaxSaving(totalTaxSaving));
      dispatch(
        updatedPensionChart({
          monthlyData,
          totalInvestment,
          totalCorpus: corpusAtRetirement,
          interestEarned,
          totalTaxSaving,
          annuityValue,
          lumpsumValue,
          monthlyPension,
        })
      );
      setCalcDone(true);
    },
  });
  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetPentionData());
    };
  }, [dispatch]);

  const currentAgeSliderRef = useRef(null);
  const retirementAgeSliderRef = useRef(null);
  const contributionYearsSliderRef = useRef(null);
  const monthlyContributionSliderRef = useRef(null);
  const annualIncreaseSliderRef = useRef(null);
  const expectedReturnSliderRef = useRef(null);
  const annuityInvestmentSliderRef = useRef(null);
  const annuityRateSliderRef = useRef(null);

  const handleCurrentAgeSliderChange = (e) => {
    const value = parseInt(e.target.value, 10);
    dispatch(setCurrentAge(value));
    formik.setFieldValue("currentAge", value);

    // Update contribution years based on retirement age
    const contributionYears = formik.values.retirementAge - value;
    formik.setFieldValue("contributionYears", contributionYears);
    dispatch(setContributionYears(contributionYears));
    setCalcDone(false);
  };

  const handleRetirementAgeSliderChange = (e) => {
    const value = parseInt(e.target.value, 10);
    dispatch(setRetirementAge(value));
    formik.setFieldValue("retirementAge", value);

    // Update contribution years based on current age
    const contributionYears = value - formik.values.currentAge;
    formik.setFieldValue("contributionYears", contributionYears);
    dispatch(setContributionYears(contributionYears));
    setCalcDone(false);
  };

  const handleContributionYearsSliderChange = (e) => {
    const value = parseInt(e.target.value, 10);
    dispatch(setContributionYears(value));
    formik.setFieldValue("contributionYears", value);

    const retirementAge = formik.values.currentAge + value;
    formik.setFieldValue("retirementAge", retirementAge);
    dispatch(setRetirementAge(retirementAge));
    setCalcDone(false);
  };

  const handleMonthlyContributionSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setMonthlyContribution(value));
    formik.setFieldValue("monthlyContribution", value);
    setCalcDone(false);
  };

  const handleAnnualIncreaseSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setAnnualIncrease(value));
    formik.setFieldValue("annualIncrease", value);
    setCalcDone(false);
  };

  const handleExpectedReturnSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setExpectedReturn(value));
    formik.setFieldValue("expectedReturn", value);
    setCalcDone(false);
  };

  const handleAnnuityInvestmentSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setAnnuityInvestment(value));
    formik.setFieldValue("annuityInvestment", value);
    setCalcDone(false);
  };

  const handleAnnuityRateSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setAnnuityRate(value));
    formik.setFieldValue("annuityRate", value);
    setCalcDone(false);
  };

  const handleContributionUnitToggle = () => {
    const currentValue = formik.values.monthlyContribution;
    if (contributionUnit === "Month") {
      setContributionUnit("Year");
      formik.setFieldValue("monthlyContribution", currentValue * 12);
    } else {
      setContributionUnit("Month");
      formik.setFieldValue(
        "monthlyContribution",
        Math.round(currentValue / 12)
      );
    }
    setCalcDone(false);
  };

  const handleContributionChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("monthlyContribution", "");
      return;
    }

    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 8) return;

    if (rawValue === "0") {
      formik.setFieldValue("monthlyContribution", 0);
      return;
    }

    formik.setFieldValue(
      "monthlyContribution",
      rawValue ? Number(rawValue) : ""
    );
    setCalcDone(false);
  };
  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
        shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
      );
  };

  const handleCurrentAgeInputChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9.,]/g, "");

    // Allow empty string while typing
    if (rawValue === "") {
      formik.setFieldValue("currentAge", "");
      return;
    }

    if (/^\d{1,2}$/.test(rawValue)) {
      const numericValue = parseFloat(
        rawValue
          .replace(/,/g, ".")
          .replace(/\./g, "")
          .replace(/(\d+)(\.\d{2})?/, "$1.$2")
      );

      if (numericValue >= 18 && numericValue <= 70) {
        formik.setFieldValue("currentAge", numericValue);
        dispatch(setCurrentAge(numericValue));

        const contributionYears = formik.values.retirementAge - numericValue;
        console.log("numericValue: ", numericValue);
        console.log("contributionYears: ", contributionYears);
        formik.setFieldValue("contributionYears", contributionYears);
        dispatch(setContributionYears(contributionYears));
      } else {
        formik.setFieldValue("currentAge", numericValue);
      }
    }
  };

  const handleRetirementAgeInputChange = (e) => {
    const value = e.target.value;

    // Allow empty string while typing
    if (value === "") {
      formik.setFieldValue("retirementAge", "");
      return;
    }

    // Only allow numeric input with max 2 digits
    if (/^\d{1,2}$/.test(value)) {
      const numValue = parseInt(value);

      // Check if value is within valid range (58-75)
      if (numValue >= 50 && numValue <= 75) {
        formik.setFieldValue("retirementAge", numValue);
        dispatch(setRetirementAge(numValue)); // Assuming you have this action

        // Update contribution years based on current age
        const contributionYears = numValue - formik.values.currentAge;
        formik.setFieldValue("contributionYears", contributionYears);
        dispatch(setContributionYears(contributionYears));
      } else {
        // Still set the value but don't update other fields for invalid range
        formik.setFieldValue("retirementAge", value);
      }
    }
    setCalcDone(false);
  };

  const handleAnnualIncreaseChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("annualIncrease", rawValue);
      dispatch(setAnnualIncrease(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("annualIncrease", normalizedValue);
    dispatch(setAnnualIncrease(normalizedValue));
    setCalcDone(false);
  };

  const handleExpectedreturnChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("expectedReturn", rawValue);
      dispatch(setExpectedReturn(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("expectedReturn", normalizedValue);
    dispatch(setExpectedReturn(normalizedValue));
    setCalcDone(false);
  };

  const handleInvestmentChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("annuityInvestment", rawValue);
      dispatch(setAnnuityInvestment(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("annuityInvestment", normalizedValue);
    dispatch(setAnnuityInvestment(normalizedValue));
    setCalcDone(false);
  };

  const handleAnnuityChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("annuityRate", rawValue);
      dispatch(setAnnuityRate(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("annuityRate", normalizedValue);
    dispatch(setAnnuityRate(normalizedValue));
    setCalcDone(false);
  };

  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {textData.pensionContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.pensionContent.description}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {" "}
        {textData.pensionContent.howItWorksTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.pensionContent.howItWorksDescription}
      </p>

      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>
            {" "}
            {textData.pensionContent.calculationPoints[0].label}:
          </strong>{" "}
          {textData.pensionContent.calculationPoints[0].description}
        </li>
        <li>
          <strong>
            {" "}
            {textData.pensionContent.calculationPoints[1].label}:
          </strong>{" "}
          {textData.pensionContent.calculationPoints[1].description}
        </li>
        <li>
          <strong>
            {" "}
            {textData.pensionContent.calculationPoints[2].label}:
          </strong>{" "}
          {textData.pensionContent.calculationPoints[2].description}
        </li>
        <li>
          <strong>
            {" "}
            {textData.pensionContent.calculationPoints[3].label}:
          </strong>{" "}
          {textData.pensionContent.calculationPoints[3].description}
        </li>
        <li>
          <strong>
            {" "}
            {textData.pensionContent.calculationPoints[4].label}:
          </strong>{" "}
          {textData.pensionContent.calculationPoints[4].description}
        </li>
        <li>
          <strong>
            {" "}
            {textData.pensionContent.calculationPoints[5].label}:
          </strong>{" "}
          {textData.pensionContent.calculationPoints[5].description}
        </li>
      </ul>

      <p className="font-medium  text-sm sm:text-base p-3 mt-3">
        <strong> {textData.pensionContent.formula}</strong>{" "}
        {textData.pensionContent.formulaNote}
        <code class=" px-1 rounded">FV = PV (1 + r)^n</code>,
        {textData.pensionContent.formulaDes}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-4">
        {" "}
        {textData.pensionContent.keyBenefitsTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong> {textData.pensionContent.keyBenefits[0].label}:</strong>{" "}
          {textData.pensionContent.keyBenefits[0].description}
        </li>
        <li>
          <strong> {textData.pensionContent.keyBenefits[1].label}:</strong>{" "}
          {textData.pensionContent.keyBenefits[1].description}
        </li>
        <li>
          <strong> {textData.pensionContent.keyBenefits[2].label}:</strong>{" "}
          {textData.pensionContent.keyBenefits[2].description}
        </li>
        <li>
          <strong> {textData.pensionContent.keyBenefits[3].label}:</strong>{" "}
          {textData.pensionContent.keyBenefits[3].description}
        </li>
        <li>
          <strong> {textData.pensionContent.keyBenefits[4].label}:</strong>{" "}
          {textData.pensionContent.keyBenefits[4].description}
        </li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-4">
        {" "}
        {textData.pensionContent.howToUseTitle}
      </h2>
      <ol className="list-decimal px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li
          dangerouslySetInnerHTML={{
            __html: textData.pensionContent.howToUseSteps[0],
          }}
        />

        <li
          dangerouslySetInnerHTML={{
            __html: textData.pensionContent.howToUseSteps[1],
          }}
        />

        <li
          dangerouslySetInnerHTML={{
            __html: textData.pensionContent.howToUseSteps[2],
          }}
        />

        <li
          dangerouslySetInnerHTML={{
            __html: textData.pensionContent.howToUseSteps[3],
          }}
        />
      </ol>

      <h2 className="text-black text-base sm:text-xl font-medium mt-4">
        {textData.pensionContent.whyStartEarlyTitle}
      </h2>
      <p class="font-medium  text-sm sm:text-base p-3 mt-3">
        {textData.pensionContent.whyStartEarlyDescription}
      </p>

      <div class="font-medium  text-sm sm:text-base p-3 mt-3">
        <strong>{textData.pensionContent.tip}</strong>{" "}
        {textData.pensionContent.tipDetail}
      </div>
    </div>
  );
  const timeLabel = t("yearShort");

  const getPlaceholderValue = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };

  return (
    <>
      <Helmet>
        <title>Pension Calculator Online – Know Your Retirement Savings</title>
        <meta
          name="description"
          content="Calculate your retirement savings online with our free Pension Calculator. Plan finances, manage funds, and secure your financial future effortlessly."
        />
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/pension-calculator"
        />

        <meta
          property="og:title"
          content="Pension Calculator Online – Know Your Retirement Savings"
        />
        <meta
          property="og:description"
          content="Calculate your retirement savings online with our free Pension Calculator. Plan finances, manage funds, and secure your financial future effortlessly."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/pension-calculator"
        />
        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />
        <meta
          property="og:image:alt"
          content="Free Pension Calculator Online for Retirement Savings - GrowLifeFund"
        />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <CalculatorLayout
        calcDone={calcDone}
        generateData={result}
        title={
          <>
            {" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.your}{" "}
            </span>
            {textData.pensionCalc.title}{" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.pensionCalc.Amount}{" "}
            </span>
          </>
        }
        description={textData.pensionCalc.description}
        descriptionContent={descriptionContent}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.pensionCalc.currentAgeLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={currentAgeSliderRef}>

                  <input
                    type="range"
                    min={18}
                    max={70}
                    step={1}
                    value={formik.values.currentAge}
                    onChange={handleCurrentAgeSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.currentAge - 18) / (70 - 18)) * 100
                        }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="currentAge"
                    leftIcon={timeLabel}
                    value={formik.values.currentAge}
                    onChange={handleCurrentAgeInputChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    placeholder={"18"}
                    borderColor={
                      formik.touched.currentAge && formik.errors.currentAge
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]  border ${formik.touched.currentAge && formik.errors.currentAge
                        ? "border-danger"
                        : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.currentAge && formik.errors.currentAge && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.currentAge}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.pensionCalc.investUpToAgeLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={retirementAgeSliderRef}
                >

                  <input
                    type="range"
                    min={50}
                    max={75}
                    step={1}
                    value={formik.values.retirementAge}
                    onChange={handleRetirementAgeSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.retirementAge - 50) / (75 - 50)) * 100
                        }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="retirementAge"
                    leftIcon={timeLabel}
                    value={formik.values.retirementAge}
                    onChange={handleRetirementAgeInputChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    placeholder={"50"}
                    borderColor={
                      formik.touched.retirementAge &&
                        formik.errors.retirementAge
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px]  border ${formik.touched.retirementAge &&
                        formik.errors.retirementAge
                        ? "border-danger"
                        : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.retirementAge &&
                    formik.errors.retirementAge && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.retirementAge}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.pensionCalc.totalContributionYearsLabel}
                </label>
                <div className="flex items-end flex-col">
                  <Input
                    name="contributionYears"
                    leftIcon={timeLabel}
                    value={formik.values.contributionYears}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    type="number"
                    placeholder={"40"}
                    borderColor={
                      formik.touched.contributionYears &&
                        formik.errors.contributionYears
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]  border ${formik.touched.contributionYears &&
                        formik.errors.contributionYears
                        ? "border-danger"
                        : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                    disabled={true}
                  />
                  {/* {formik.touched.contributionYears &&
                    formik.errors.contributionYears && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.contributionYears}
                      </div>
                    )} */}
                </div>
              </div>
            </div>

            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <div className="flex  flex-row items-start gap-1">
                  <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                    {textData.pensionCalc.contributionAmountLabel}
                  </label>
                  <div
                    className="ml-0 sm:ml-2 flex items-center cursor-pointer"
                    onClick={handleContributionUnitToggle}
                  >
                    <div className="relative   w-full flex items-center gap-[2px]">
                      <div className="text-primary text-sm   md:text-sm font-normal ">
                        {timeUnits[contributionUnit]}
                      </div>
                      <div className="absolute -bottom-1 left-0 right-0 h-0.5 "></div>
                      <img
                        src={arrowImage}
                        alt="Dropdown arrow"
                        className="w-3 h-3 sm:w-4 sm:h-4"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={monthlyContributionSliderRef}
                >

                  <input
                    type="range"
                    min={100}
                    max={1000000}
                    step={100}
                    value={formik.values.monthlyContribution}
                    onChange={handleMonthlyContributionSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.monthlyContribution - 100) /
                          (1000000 - 100)) *
                        100
                        }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-end flex-col">
                  <Input
                    name="monthlyContribution"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.monthlyContribution)}
                    onChange={handleContributionChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderValue(i18n.language)}
                    borderColor={
                      formik.touched.monthlyContribution &&
                        formik.errors.monthlyContribution
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]  border ${formik.touched.monthlyContribution &&
                        formik.errors.monthlyContribution
                        ? "border-danger"
                        : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.monthlyContribution &&
                    formik.errors.monthlyContribution && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.monthlyContribution}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.pensionCalc.annualIncreaseLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={annualIncreaseSliderRef}
                >

                  <input
                    type="range"
                    min={0}
                    max={15}
                    step={0.1}
                    value={parseFloat(formik.values.annualIncrease)}
                    onChange={handleAnnualIncreaseSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${(formik.values.annualIncrease / 15) * 100}%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-end flex-col">
                  <Input
                    name="annualIncrease"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("56")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.annualIncrease
                    )}
                    onChange={handleAnnualIncreaseChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.annualIncrease &&
                        formik.errors.annualIncrease
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]  border ${formik.touched.annualIncrease &&
                        formik.errors.annualIncrease
                        ? "border-danger"
                        : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.annualIncrease &&
                    formik.errors.annualIncrease && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.annualIncrease}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.pensionCalc.expectedReturnLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={expectedReturnSliderRef}
                >

                  <input
                    type="range"
                    min={5}
                    max={15}
                    step={0.1}
                    value={parseFloat(formik.values.expectedReturn)}
                    onChange={handleExpectedReturnSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.expectedReturn - 5) / (15 - 5)) * 100
                        }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-end flex-col">
                  <Input
                    name="expectedReturn"
                    leftIcon="%"
                    onChange={handleExpectedreturnChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("6.5")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.expectedReturn
                    )}
                    borderColor={
                      formik.touched.expectedReturn &&
                        formik.errors.expectedReturn
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]  border ${formik.touched.expectedReturn &&
                        formik.errors.expectedReturn
                        ? "border-danger"
                        : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.expectedReturn &&
                    formik.errors.expectedReturn && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.expectedReturn}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.pensionCalc.investInAnnuityLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={annuityInvestmentSliderRef}
                >

                  <input
                    type="range"
                    min={40}
                    max={99}
                    step={0.1}
                    value={parseFloat(formik.values.annuityInvestment)}
                    onChange={handleAnnuityInvestmentSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.annuityInvestment - 40) / (99 - 40)) *
                        100
                        }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-end flex-col">
                  <Input
                    name="annuityInvestment"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("56")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.annuityInvestment
                    )}
                    onChange={handleInvestmentChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.annuityInvestment &&
                        formik.errors.annuityInvestment
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]  border ${formik.touched.annuityInvestment &&
                        formik.errors.annuityInvestment
                        ? "border-danger"
                        : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.annuityInvestment &&
                    formik.errors.annuityInvestment && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.annuityInvestment}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div >
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.pensionCalc.annuityRateLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={annuityRateSliderRef}>

                  <input
                    type="range"
                    min={4}
                    max={10}
                    step={0.1}
                    value={parseFloat(formik.values.annuityRate)}
                    onChange={handleAnnuityRateSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.annuityRate - 4) / (10 - 4)) * 100
                        }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-end flex-col">
                  <Input
                    name="annuityRate"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("56")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.annuityRate
                    )}
                    onChange={handleAnnuityChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.annuityRate && formik.errors.annuityRate
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]  border ${formik.touched.annuityRate && formik.errors.annuityRate
                        ? "border-danger"
                        : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.annuityRate && formik.errors.annuityRate && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.annuityRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-[10px] relative">
              <Button type="submit" className="w-full mb-2">
                {textData.calculate}
              </Button>
              {pensionErrors && (
                <div className=" absolute bottom-[-50] z-50 w-full text-red-500 mb-4 text-center px-2 py-2 sm:py-4  rounded-md text-black bg-gray-light border-none text-sm">
                  {pensionErrors}
                </div>
              )}
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default PensionCalc;
